import { consola } from 'consola'
import { useAuthStore } from './useAuthStore'
import { useLogoutHandler } from './useLogoutHandler'

const logger = consola.withTag('auth-monitor')

const PROTECTED_ROUTES = [
  '/lk',
  '/lk/personal-information',
  '/lk/activation-history',
  '/lk/safety',
  '/favorites',
]

let isRedirecting = false

/** Вынесено для покрытия тестами (watch на sessionStorage сам по себе не реактивен в том же табе). */
/** Отдельно от watch — проще покрыть тестами и избежать дублирования. */
export async function refreshSessionTokenViaV2(): Promise<unknown> {
  const { useTokenRefreshV2 } = await import('./useTokenRefreshV2')
  return useTokenRefreshV2().refreshToken()
}

export async function runAuthSessionIdRecovery(
  prevSessionId: string | null | undefined,
  sessionId: string | null,
  pathname: string,
  deps: {
    isProtectedRoute: (route: string) => boolean
    isTokenRefreshing: () => boolean
    refreshToken: () => Promise<unknown>
    redirectToHome: () => void
  },
): Promise<void> {
  if (!prevSessionId || sessionId || !deps.isProtectedRoute(pathname)) return
  try {
    if (deps.isTokenRefreshing()) return
    await deps.refreshToken()
  } catch {
    deps.redirectToHome()
  }
}

export const useAuthMonitor = () => {
  const authStore = useAuthStore()
  const logoutHandler = useLogoutHandler()

  const isProtectedRoute = (route: string): boolean => {
    return PROTECTED_ROUTES.some(protectedRoute => route.startsWith(protectedRoute))
  }

  const redirectToHome = (): void => {
    if (isRedirecting) return
    isRedirecting = true
    if (process.client) window.location.href = '/'
  }

  const startAuthMonitoring = () => {
    /* v8 ignore next 2 */
    if (!process.client) return

    const unwatch = watch(
      () => authStore.isLoggedIn,
      (isLoggedIn, wasLoggedIn) => {
        if (wasLoggedIn && !isLoggedIn && isProtectedRoute(window.location.pathname)) {
          logger.warn('User logged out on protected route')
          redirectToHome()
        }
      },
      { immediate: true },
    )

    const unwatchTokens = watch(
      () => /* v8 ignore next */ process.client ? sessionStorage.getItem('authSessionId') : null,
      (sessionId, prevSessionId) => {
        void runAuthSessionIdRecovery(prevSessionId, sessionId, window.location.pathname, {
          isProtectedRoute,
          isTokenRefreshing: logoutHandler.isTokenRefreshing,
          refreshToken: refreshSessionTokenViaV2,
          redirectToHome,
        })
      },
      { immediate: true },
    )

    return () => {
      unwatch()
      unwatchTokens()
    }
  }

  const checkCurrentAuthState = (): void => {
    const currentRoute = /* v8 ignore next */ process.client ? window.location.pathname : ''
    if (isProtectedRoute(currentRoute) && !authStore.isLoggedIn) {
      redirectToHome()
    }
  }

  return {
    startAuthMonitoring,
    checkCurrentAuthState,
    isProtectedRoute,
    protectedRoutes: PROTECTED_ROUTES,
  }
}

